import type {
    AreaMapObject,
    OrientationRpy,
    Point2D,
    Point3D,
} from "@corvus/corvus-map";
import {
    type MeasurementPoseMode,
    type MeasurementRaw,
    resolveMeasurementPose,
} from "@corvus/corvus-map/model";

interface RawCameraMatrix {
    width?: unknown;
    height?: unknown;
    focal_x?: unknown;
    focal_y?: unknown;
    c_x?: unknown;
    c_y?: unknown;
}

export interface MeasurementMosaicSource extends MeasurementRaw {
    meta: MeasurementRaw["meta"] & {
        payload?: {
            matrix?: RawCameraMatrix | null;
        } | null;
    };
}

export type MeasurementImageCorners = readonly [
    Point2D,
    Point2D,
    Point2D,
    Point2D,
];

const RAY_PLANE_EPSILON = 1e-6;

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

const distanceSquaredToArea = (
    point: Point2D,
    area: AreaMapObject,
): number | null => {
    if (
        !isFiniteNumber(area.x) ||
        !isFiniteNumber(area.y) ||
        !isFiniteNumber(area.width) ||
        !isFiniteNumber(area.height) ||
        area.width < 0 ||
        area.height < 0
    ) {
        return null;
    }

    const maxX = area.x + area.width;
    const maxY = area.y + area.height;
    if (!Number.isFinite(maxX) || !Number.isFinite(maxY)) return null;

    const xDistance =
        point.x < area.x
            ? area.x - point.x
            : point.x > maxX
              ? point.x - maxX
              : 0;
    const yDistance =
        point.y < area.y
            ? area.y - point.y
            : point.y > maxY
              ? point.y - maxY
              : 0;

    return xDistance * xDistance + yDistance * yDistance;
};

/** Returns the first nearest valid area Z, preserving input order for equal distances. */
export function getMeasurementGroundZ(
    position: Point2D,
    areas: readonly AreaMapObject[],
): number {
    let nearestZ = 0;
    let nearestDistanceSquared = Infinity;

    for (const area of areas) {
        if (!isFiniteNumber(area.z)) continue;

        const distanceSquared = distanceSquaredToArea(position, area);
        if (
            distanceSquared === null ||
            distanceSquared >= nearestDistanceSquared
        )
            continue;

        nearestZ = area.z;
        nearestDistanceSquared = distanceSquared;
    }

    return nearestZ;
}

interface CameraProjection {
    width: number;
    height: number;
    focalX: number;
    focalY: number;
    centerX: number;
    centerY: number;
    roll: number;
    pitch: number;
    yaw: number;
}

const readCameraProjection = (
    measurement: MeasurementMosaicSource,
    orientation: OrientationRpy,
): CameraProjection | null => {
    const matrix = measurement.meta?.payload?.matrix;

    if (
        !matrix ||
        !isFiniteNumber(matrix.width) ||
        !isFiniteNumber(matrix.height) ||
        !isFiniteNumber(matrix.focal_x) ||
        !isFiniteNumber(matrix.focal_y) ||
        !isFiniteNumber(matrix.c_x) ||
        !isFiniteNumber(matrix.c_y) ||
        matrix.width <= 0 ||
        matrix.height <= 0 ||
        matrix.focal_x <= 0 ||
        matrix.focal_y <= 0
    ) {
        return null;
    }

    return {
        width: matrix.width,
        height: matrix.height,
        focalX: matrix.focal_x,
        focalY: matrix.focal_y,
        centerX: matrix.c_x,
        centerY: matrix.c_y,
        roll: orientation.r,
        pitch: orientation.p,
        yaw: orientation.y - Math.PI / 2,
    };
};

const cameraRayToWorld = (
    pixel: Point2D,
    projection: CameraProjection,
): Point3D => {
    const cameraX = (pixel.x - projection.centerX) / projection.focalX;
    const cameraY = (pixel.y - projection.centerY) / projection.focalY;

    // Established Corvus convention: R_yaw * R_pitch * R_roll, where source roll
    // rotates around camera Y, pitch around X, and adjusted yaw around world Z.
    const rollCos = Math.cos(projection.roll);
    const rollSin = Math.sin(projection.roll);
    const rollX = rollCos * cameraX + rollSin;
    const rollY = cameraY;
    const rollZ = -rollSin * cameraX + rollCos;

    const pitchCos = Math.cos(projection.pitch);
    const pitchSin = Math.sin(projection.pitch);
    const pitchX = rollX;
    const pitchY = pitchCos * rollY - pitchSin * rollZ;
    const pitchZ = pitchSin * rollY + pitchCos * rollZ;

    const yawCos = Math.cos(projection.yaw);
    const yawSin = Math.sin(projection.yaw);
    const rotatedX = yawCos * pitchX + yawSin * pitchY;
    const rotatedY = -yawSin * pitchX + yawCos * pitchY;

    // Camera +Z points toward the ground. These axis signs preserve the existing
    // Corvus pixel-to-world convention while representing the ray as downward.
    return { x: -rotatedX, y: rotatedY, z: -pitchZ };
};

const intersectGroundPlane = (
    cameraPosition: Point3D,
    ray: Point3D,
    groundZ: number,
): Point2D | null => {
    if (Math.abs(ray.z) <= RAY_PLANE_EPSILON) return null;

    const distance = (groundZ - cameraPosition.z) / ray.z;
    if (!Number.isFinite(distance) || distance <= 0) return null;

    const x = cameraPosition.x + distance * ray.x;
    const y = cameraPosition.y + distance * ray.y;

    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
};

export function projectMeasurementImage(
    measurement: MeasurementMosaicSource,
    areas: readonly AreaMapObject[],
    poseMode: MeasurementPoseMode,
): MeasurementImageCorners | null {
    const resolvedPose = resolveMeasurementPose(measurement, poseMode);
    if (!resolvedPose) return null;

    const { position: cameraPosition, orientation } = resolvedPose.pose;
    const projection = readCameraProjection(measurement, orientation);
    if (!projection) return null;

    const groundZ = getMeasurementGroundZ(cameraPosition, areas);
    const pixelCorners: readonly [Point2D, Point2D, Point2D, Point2D] = [
        { x: 0, y: 0 },
        { x: projection.width, y: 0 },
        { x: projection.width, y: projection.height },
        { x: 0, y: projection.height },
    ];
    const [topLeft, topRight, bottomRight, bottomLeft] = pixelCorners.map(
        (pixel) =>
            intersectGroundPlane(
                cameraPosition,
                cameraRayToWorld(pixel, projection),
                groundZ,
            ),
    );

    if (!topLeft || !topRight || !bottomRight || !bottomLeft) return null;

    return [topLeft, topRight, bottomRight, bottomLeft];
}

export function getMeasurementThumbnailPath(
    organizationId: string,
    locationId: string,
    measurementId: string,
): string {
    return `organizations/${organizationId}/locations/${locationId}/measurements/256x256/${measurementId}_256x256.jpg`;
}

export function getMeasurementDetailPath(
    organizationId: string,
    locationId: string,
    measurementId: string,
): string {
    return `organizations/${organizationId}/locations/${locationId}/measurements/${measurementId}.jpg`;
}
